const ACT_CODE = { 1: '1', 2: '2a', 3: '2b', 4: '3' };

export function actCodeFromNumber(number) {
  return ACT_CODE[Number(number)] || String(number || '');
}

export function actLabelFromNumber(number) {
  const code = actCodeFromNumber(number);
  return code ? `Act ${code}` : 'Act';
}

export function nodeContent(unit) {
  return unit?.detailedContent || unit || {};
}

export function unitSortKey(unit) {
  const dc = nodeContent(unit);
  const raw = dc.sortKey ?? dc.sort_key ?? unit?.sortKey ?? unit?.sort_key ?? dc.number;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export function unitDisplayLabel(unit, context = 'list') {
  const dc = nodeContent(unit);
  const type = dc.type || unit?.nodeType || dc.grain;
  if (context === 'wheel' && (dc.wheelLabel || dc.wheel_label)) return dc.wheelLabel || dc.wheel_label;
  if (context === 'short' && (dc.shortLabel || dc.short_label)) return dc.shortLabel || dc.short_label;
  if (context === 'compact') {
    if (type === 'sequence') return dc.code || dc.sequenceCode || dc.sequence_code || dc.wheelLabel?.replace(/^Seq\s+/i, '') || dc.wheel_label?.replace(/^Seq\s+/i, '') || String(dc.number || '');
    if (type === 'act') return dc.actCode || dc.act_code || dc.code || String(dc.number || '');
    if (type === 'scene') return dc.number != null ? String(dc.number) : (dc.label || 'Scene');
  }
  if ((context === 'list' || context === 'table') && (dc.listLabel || dc.list_label)) return dc.listLabel || dc.list_label;
  if (context === 'breadcrumb') {
    if (type === 'sequence' && (dc.shortLabel || dc.short_label)) return dc.shortLabel || dc.short_label;
    if (type === 'act' && (dc.actLabel || dc.act_label || dc.code || dc.actCode || dc.act_code)) return dc.actLabel || dc.act_label || `Act ${dc.code || dc.actCode || dc.act_code}`;
  }
  if (dc.displayLabel || dc.display_label) return dc.displayLabel || dc.display_label;
  if (type === 'act') return dc.actLabel || dc.act_label || (dc.code ? `Act ${dc.code}` : actLabelFromNumber(dc.number));
  if (type === 'sequence') {
    const short = dc.shortLabel || dc.short_label || (dc.number != null ? `Seq ${dc.number}` : 'Seq');
    const act = dc.actLabel || dc.act_label;
    return context === 'short' || !act ? short : `${act}, ${short}`;
  }
  if (type === 'scene') return dc.number != null ? `Scene ${dc.number}` : (dc.title || 'Scene');
  if (type === 'movie' || type === 'script') return 'Script';
  return dc.label || dc.title || '';
}

export function unitTitle(unit) {
  const dc = nodeContent(unit);
  const label = unitDisplayLabel(unit, 'list');
  const title = dc.title || '';
  return title && title !== label ? title : '';
}

export function unitChipLabel(unit) {
  if (unit == null) return '';
  if (typeof unit === 'string' || typeof unit === 'number') return String(unit);
  return unit.label || unit.listLabel || unit.list_label || unit.displayLabel || unit.display_label || unit.shortLabel || unit.short_label || unit.id || '';
}

export function unitTextCandidates(unit) {
  const dc = nodeContent(unit);
  return [
    unit?.id,
    dc.id,
    dc.unitId,
    dc.unit_id,
    dc.ref,
    dc.label,
    unit?.label,
    dc.title,
    dc.code,
    dc.sequenceCode,
    dc.sequence_code,
    dc.displayLabel,
    dc.display_label,
    dc.listLabel,
    dc.list_label,
    dc.shortLabel,
    dc.short_label,
    dc.wheelLabel,
    dc.wheel_label,
    unitDisplayLabel(unit, 'list'),
    unitDisplayLabel(unit, 'short'),
    unitDisplayLabel(unit, 'wheel'),
    unitDisplayLabel(unit, 'compact'),
  ].filter(v => v != null && String(v).trim() !== '').map(v => String(v).trim());
}

export function normalizeUnitText(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

// ── the sequence cell's label on the timeline ─────────────────────────────────────────────────
// ⚠️ WHAT `ordinal` IS. Icicle computes a POSITIONAL index for each sequence — 1-based within its
// parent act, from sort order. `sequences.number` never reaches the screen. For a screenplay that
// is correct and remains the default.
//
// It is NOT correct for a stage play, where these rows are the playwright's printed scenes and the
// reader will look the number up in their own copy. A playwright can start at 2, restart mid-act,
// print the same number twice, or number in romans; a positional index silently renumbers all of
// it. So a server-supplied label wins, and everything without one keeps the ordinal it had.
//
// Pure on purpose: these used to be closures inside the Icicle component and so could not be
// tested. unitLabels.selftest.mjs exercises them.
export function sequenceCellLabel(unit, ordinal) {
  const dc = nodeContent(unit);
  const supplied = dc.wheelLabel || dc.wheel_label || dc.shortLabel || dc.short_label;
  if (supplied) return String(supplied);
  return ordinal == null ? '' : `Seq ${ordinal}`;
}

/** The narrow-cell form: the bare identifier, no noun. */
export function sequenceCellCode(unit, ordinal) {
  const dc = nodeContent(unit);
  const code = dc.sequenceCode || dc.sequence_code;
  if (code) return String(code);
  // ⚠️ WITH A SUPPLIED LABEL, THE CODE COMES OUT OF THE LABEL — NEVER OUT OF THE ORDINAL.
  // Same argument as sequenceCellLabel above, one step further. A supplied label means this row is
  // the playwright's own division, so its identifier is whatever they PRINTED. Two cases, and the
  // ordinal is wrong in both:
  //   • 'Sc 7' with no code   -> '7'. Falling back to a positional 2 shows a number from a
  //     DIFFERENT numbering than the one on the page in the reader's hand — the exact silent
  //     renumbering the note above this pair exists to forbid.
  //   • 'Prologue'            -> ''.  They printed no number at all. `three_musketeers` opens on
  //     one, and its 39px cell (under the 46px full-label floor, over the 15px code floor) rendered
  //     our monotonic `sequences.number`: it read "1", immediately left of a real "Sc 1". Two
  //     different units, same number, one of them invented. A cell too narrow to say something true
  //     shows nothing — which is already what the scene row does below its own floor.
  const supplied = dc.wheelLabel || dc.wheel_label || dc.shortLabel || dc.short_label;
  if (supplied) {
    const m = String(supplied).match(/[\s.]([0-9]+|[IVXLCDM]+)\s*$/i);
    return m ? m[1] : '';
  }
  return ordinal == null ? '' : String(ordinal);
}
